import styles from './OnlineToolsTeaser.module.scss';
import classNames from 'classnames';
import { Button, TYPE } from '@common/Button/Button';
import { Icon, IconColor, IconID } from '@common/Icon/Icon';
import { WidenImage } from '@common/Widen/WidenImage';
import { VIEW_TYPE } from '@hooks/usePersistedViewType';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { getOnlineToolsTeaserData } from '@libs/online-tools-teaser.helper';

export interface OnlineToolsTeaserProps {
  viewType: VIEW_TYPE;
  selectedCategoryKey: string;
}

export const OnlineToolsTeaser = ({ viewType, selectedCategoryKey }: OnlineToolsTeaserProps) => {
  const { t } = useTranslation(['category/onlineToolsTeaser']);
  const { query } = useRouter();

  const onlineToolsTeaserData = getOnlineToolsTeaserData(selectedCategoryKey.toLowerCase());

  const baseUrl = onlineToolsTeaserData?.utmParameter;
  const [url, setUrl] = useState(baseUrl);

  useEffect(() => {
    let tempUrl = baseUrl;
    if (query) {
      for (const [key, value] of Object.entries(query)) {
        if (!['categoryL1', 'categoryL2', 'page'].includes(key)) {
          tempUrl += `&${key}=${value as string}`;
        }
      }
    }
    setUrl(tempUrl);
  }, [query]);

  if (!onlineToolsTeaserData) {
    return null;
  }

  return (
    <div
      className={classNames(
        'border',
        viewType === VIEW_TYPE.LIST ? 'mb-12' : 'mb-6 sm:m-2',
        'p-12',
        'flex-auto',
        'w-full',
        viewType === VIEW_TYPE.LIST ? 'order-2' : 'sm:order-1 lg:order-3',
        styles.onlineToolsTeaserBackground
      )}
      data-testid="online-tools-teaser"
    >
      <h2 className="lg:mb-8 text-gray-500 font-medium text-4xl">
        {t(
          [
            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
            `PRODUCT_OVERVIEW.ONLINE_TOOLS_TEASER.${onlineToolsTeaserData.group.toUpperCase()}.${selectedCategoryKey.toUpperCase()}.HEADLINE`,
            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
            `PRODUCT_OVERVIEW.ONLINE_TOOLS_TEASER.${onlineToolsTeaserData.group.toUpperCase()}.HEADLINE`,
          ],
          { category: selectedCategoryKey.toUpperCase() }
        )}
      </h2>

      <div className="flex flex-col lg:flex-row lg:mr-8">
        <div className="flex flex-col">
          <div className="flex flex-col lg:flex-row">
            <div className="w-full sm:w-1/2 lg:w-1/3">
              <div className="mr-4">
                <a href={url} data-testid="online-tools-teaser-image-link">
                  <WidenImage
                    shortCode={onlineToolsTeaserData?.shortcode}
                    title={onlineToolsTeaserData?.title}
                    filename={onlineToolsTeaserData?.image}
                    width={300}
                    height={300}
                    layout="intrinsic"
                  />
                </a>
              </div>
            </div>
            <div className="lg:w-2/3 lg:pr-6 lg:mt-12 lg:pt-2 text-sm">
              <ul
                className={classNames(styles.onlineToolsTeaserBulletList)}
                data-testid="online-tools-teaser-html"
                dangerouslySetInnerHTML={{
                  __html: t(
                    [
                      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                      `PRODUCT_OVERVIEW.ONLINE_TOOLS_TEASER.${onlineToolsTeaserData.group.toUpperCase()}.${selectedCategoryKey.toUpperCase()}.LIST`,
                      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                      `PRODUCT_OVERVIEW.ONLINE_TOOLS_TEASER.${onlineToolsTeaserData.group.toUpperCase()}.LIST`,
                    ],
                    { category: selectedCategoryKey.toUpperCase() }
                  ),
                }}
              />

              <Button
                icon={<Icon size={7} color={IconColor.WHITE} id={IconID.EXPERT} />}
                className="sm:w-auto mt-10 pr-10 pl-8"
                text={t(
                  [
                    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions,@typescript-eslint/no-unsafe-call
                    `PRODUCT_OVERVIEW.ONLINE_TOOLS_TEASER.${onlineToolsTeaserData.group.toUpperCase()}.${selectedCategoryKey.toUpperCase()}.BUTTON_TEXT`,
                    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions,@typescript-eslint/no-unsafe-call
                    `PRODUCT_OVERVIEW.ONLINE_TOOLS_TEASER.${onlineToolsTeaserData.group.toUpperCase()}.BUTTON_TEXT`,
                  ],
                  { category: selectedCategoryKey.toUpperCase() }
                )}
                type={TYPE.PRIMARY}
                href={url}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
